import React, { useCallback, useEffect, useLayoutEffect } from "react";
import { ArtistType } from "./DraftFormArtistPart";
import { PartialNullable } from "../../../types";
import { DraftAlbum, LocalizedString, RegionSettings, TrackReleaseSet } from "../../../api/useMusicApi";
import { UpdateFields } from "../../../hooks/use-advanced-state";
import { ValidationError, ValidationInputWrapper } from "../../../components/ValidationInputWrapper";
import { useTranslation } from "../../../hooks/useTranslation";
import cn from "classnames";
import { Input } from "../../../components/Input";
import { Button } from "../../../components/Button";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { faAdd } from "@fortawesome/free-solid-svg-icons/faAdd";
import _ from "lodash";
import { faExternalLink } from "@fortawesome/free-solid-svg-icons";
import { faCopy } from "@fortawesome/free-solid-svg-icons/faCopy";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";

type Props = {
  type: ArtistType;
  disabled?: boolean;
  draft: PartialNullable<DraftAlbum | TrackReleaseSet>;
  onChange: UpdateFields<DraftAlbum | TrackReleaseSet>;
  validationErrorPrefix?: string;
  validationErrors: ValidationError[];
};
export const DraftFormArtist: React.FC<Props> = ({
                                                   type,
                                                   draft,
                                                   disabled,
                                                   onChange,
                                                   validationErrorPrefix,
                                                   validationErrors
                                                 }) => {
  const { t } = useTranslation();

  const copyToClipboard = async (text: string) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      toast.success(t("link_copy"));
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0%";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      //@ts-ignore
      document.execCommand("copy");
      document.body.removeChild(textarea);
      toast.success(t("link_copy"));
    }
  };

  useEffect(() => {
    if (!disabled && ((draft?.country_meta ?? {})?.artists?.[type]?.length ?? 0) === 0) {
      // @ts-ignore
      onChange((prevState) => {
        return {
          ...prevState,
          country_meta: {
            ...(prevState.country_meta ?? {}),
            artists: {
              ...(prevState.country_meta?.artists ?? {}),
              [type]: [{} as any]
            }
          }
        };
      });
    }
  }, [draft?.country_meta?.artists?.[type]]);

  useLayoutEffect(() => {
    if ((draft?.country_meta ?? {})?.artists?.[type]?.length !== 0) {
      // @ts-ignore
      onChange((prevState) => {
        return {
          ...prevState,
          country_meta: {
            ...(prevState.country_meta ?? {}),
            artists: {
              ...(prevState.country_meta?.artists ?? {}),
              [type]: prevState?.country_meta?.artists?.[type]?.map(x => {
                if (x.front_key == null) {
                  x.front_key = _.random(1, 999, false).toString();
                }
                return x;
              })

            }
          }
        };
      });
    }
  }, []);

  const availabilityCheck = draft?.country_meta?.artists?.[type]?.length;
  const handleArtistChangeByIndex = useCallback(
    (value: string, updateIndex: number, key: keyof LocalizedString) => {
      onChange((prevState) => {
        const oldArtists = prevState?.country_meta?.artists?.[type] ?? [];
        const updatedArtists = oldArtists.map((a, index) => {
          if (index !== updateIndex) {
            return a;
          }

          return { ...a, [key]: value.trim() };
        });
        return {
          ...(prevState ?? {}),
          country_meta: {
            ...(prevState.country_meta ?? {}),
            artists: {
              ...(prevState?.country_meta?.artists ?? {}),
              [type]: updatedArtists
            }
          }
        } as any;
      });
    },
    [onChange]
  );

  const handleDeleteArtist = useCallback((updateIndex: number) => {
    onChange((prevState) => {
      const oldArtists = prevState?.country_meta?.artists?.[type] ?? [];
      const updatedArtists = oldArtists.filter((a, index) => {
        return index !== updateIndex;
      });

      return {
        ...(prevState ?? {}),
        country_meta: {
          ...(prevState.country_meta ?? {}),
          artists: {
            ...(prevState?.country_meta?.artists ?? {}),
            [type]: updatedArtists
          }
        }
      } as any;
    });
  }, []);

  const handleAddArtist = useCallback(() => {
    onChange((prevState) => {
      const oldArtists = prevState?.country_meta?.artists?.[type] ?? [];
      return {
        ...(prevState ?? {}),
        country_meta: {
          ...(prevState.country_meta ?? {}),
          artists: {
            ...(prevState?.country_meta?.artists ?? {}),
            [type]: [...oldArtists, {
              front_key: _.random(1, 999, false).toString()
            }]
          }
        }
      } as any;
    });
  }, []);

  return (
    <>
      {availabilityCheck ? (
        <table className={cn("w-full")}>
          {draft?.country_meta?.artists?.[type]?.length !== 0 && (
            <thead className={"font-bold text-xs"}>
            <tr>
              <td className={"pr-2"}>
                {t("artist")}{" "}
                <span className={"text-red-500 font-bold"}> *</span>
              </td>
              <td className={"px-2"}>VK</td>
              <td className={"px-2"} />
            </tr>
            </thead>
          )}
          <tbody>
          {draft?.country_meta?.artists?.[type]?.map((a, index) => {
            return (
              <tr key={a.front_key} className={"my-2"}>
                <td style={{ verticalAlign: "top" }}>
                  <ValidationInputWrapper
                    attribute={`${
                      validationErrorPrefix ? `${validationErrorPrefix}.` : ""
                    }country_meta.artists.${type}.${index}.ALL`}
                    validationErrors={validationErrors}
                  >
                    <Input
                      disabled={disabled}
                      defaultValue={a[RegionSettings.ALL] ?? ""}
                      placeholder={t("artist_placeholder").toString()}
                      validationError={
                        validationErrors?.find(
                          (e) =>
                            e.attr ===
                            `${
                              validationErrorPrefix
                                ? `${validationErrorPrefix}.`
                                : ""
                            }country_meta.artists.${type}.${index}.ALL`
                        )?.detail
                      }
                      onChange={(value) =>
                        handleArtistChangeByIndex(
                          value,
                          index,
                          RegionSettings.ALL
                        )
                      }
                    />
                  </ValidationInputWrapper>
                </td>
                <td style={{ verticalAlign: "top" }} className={"pl-2 pr-1"}>
                  <ValidationInputWrapper
                    attribute={`${
                      validationErrorPrefix
                        ? `${validationErrorPrefix}.`
                        : ""
                    }country_meta.artists.${type}.${index}.vk_url`}
                    validationErrors={validationErrors}
                  >
                    <Input
                      disabled={disabled}
                      defaultValue={a.vk_url ?? ""}
                      placeholder={"VK URL"}
                      validationError={
                        validationErrors?.find(
                          (e) =>
                            e.attr ===
                            `${
                              validationErrorPrefix
                                ? `${validationErrorPrefix}.`
                                : ""
                            }country_meta.artists.${type}.${index}.vk_url`
                        )?.detail
                      }
                      onChange={(value) =>
                        handleArtistChangeByIndex(value, index, "vk_url")
                      }
                    />
                  </ValidationInputWrapper>
                </td>
                {!disabled && (
                  <td style={{ verticalAlign: "top" }}>
                    <Button
                      icon={faTrash}
                      onClick={() => handleDeleteArtist(index)}
                    />
                  </td>
                )}
                {disabled && (
                  <td style={{ verticalAlign: "top" }}>
                    {
                      a.vk_url != null && <>
                        <a href={a.vk_url ?? ""} className={"block mb-[4px]"} target="_blank" rel="noopener noreferrer">
                          <FontAwesomeIcon className={"text-blue-600 cursor-pointer"} icon={faExternalLink} size={"xs"} />
                        </a>

                        <FontAwesomeIcon
                          icon={faCopy}
                          className={"block text-muted cursor-pointer"} size={"xs"}
                          onClick={() => copyToClipboard(a.vk_url ?? "")} />
                      </>
                    }
                  </td>
                )}
              </tr>
            );
          })}
          </tbody>
        </table>
      ) : (
        <div className={"w-full text-center mt-[24px]"}>{t("notFound")}</div>
      )}

      {!disabled && (
        <div className={"w-full"}>
          <Button
            icon={faAdd}
            className={"mt-2 px-2 py-2 text-xs"}
            label={`${t("add")}`}
            onClick={handleAddArtist}
          />
        </div>
      )}
    </>
  );
};
